import { StationSwitcherContext } from '@components/providers/station/context.tsx';
import { IVehiclesCapacities, Route, Simulation } from '@helpers/types.ts';
import { useDownload } from '@hooks/use-download.ts';
import { useHttp } from '@hooks/use-http';
import { find } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export const useSimulationDetail = () => {
  const http = useHttp();
  const { downloadXlsx } = useDownload();
  const { station } = useContext(StationSwitcherContext);
  const { id } = useParams();
  const [plan, setPlan] = useState<Simulation | null>(null);
  const [error, setError] = useState<string>('');
  const [selected, setSelected] = useState<Array<string>>([]);
  const [show_induction, setShowInduction] = useState<boolean>(false);
  const [loadingInduction, setLoadingInduction] = useState<boolean>(false);
  const [forSplit, setForSplit] = useState<Route | null>(null);
  const [showJoinRoutes, setShowJoinRoutes] = useState<boolean>(false);
  const [joinRoutesIsLoading, setJoinRoutesIsLoading] =
    useState<boolean>(false);
  const [disableLoading, setDisableLoading] = useState<boolean>(false);
  const [forDisableRoute, setForDisableRoute] = useState<string | null>(null);
  const [stationCoordinates, setStationCoordinates] = useState<
    [number, number] | null
  >(null);

  const onDownloadPlan = useCallback(() => {
    if (!plan) return;
    const rows: any[] = [];
    plan.routes.forEach((x) => {
      x.orders.forEach((y) => {
        const inducted = find(
          plan.containerized || [],
          (z) => z.tracking_id === y.tracking_id,
        );
        rows.push({
          plan: x.name,
          tracking_id: y.tracking_id,
          internal_key: y.internal_key,
          stop: y.delivery_order,
          lat: y.geometry.coordinates[1],
          lon: y.geometry.coordinates[0],
          inducido: inducted ? 'SI' : 'NO',
          desactivado: x.disabled ? 'SI' : 'NO',
        });
      });
    });
    downloadXlsx(rows, plan.plan_name);
  }, [plan, downloadXlsx]);

  const onLoadPlan = useCallback(
    async (plan_id: string) => {
      setPlan(null);
      setSelected([]);
      setStationCoordinates(null);
      try {
        const data = await http.lastmile.getPlan(plan_id);
        setPlan(data);
        const baseUrl = (import.meta as any).env.VITE_LASTMILE_URL as
          | string
          | undefined;
        if (baseUrl && data.station) {
          const res = await fetch(
            `${baseUrl}/station/${encodeURIComponent(data.station)}`,
          );
          if (res.ok) {
            const stationData = await res.json();
            const lon =
              stationData.location?.lon ?? stationData.location?.longitude;
            const lat =
              stationData.location?.lat ?? stationData.location?.latitude;
            if (typeof lon === 'number' && typeof lat === 'number') {
              setStationCoordinates([lon, lat]);
            }
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message);
      }
    },
    [http],
  );

  const onDownloadOutPlan = useCallback(() => {
    if (!plan) return;
    let rows: any[] = [];
    plan!.out_routes.forEach((zone) => {
      rows = [
        ...rows,
        ...zone.orders.map((order) => ({
          zona: zone.name,
          tracking_id: order.tracking_id,
          internal_key: order.internal_key,
          lat: order.geometry.coordinates[1],
          lon: order.geometry.coordinates[0],
        })),
      ];
    });
    downloadXlsx(rows, plan.plan_name);
  }, [plan, downloadXlsx]);

  const orders = useMemo(() => {
    const total_orders = plan?.routes.reduce(
      (acc, route) => acc + route.orders.length,
      0,
    );
    const total_out_orders = plan?.out_routes.reduce(
      (acc, route) => acc + route.orders.length,
      0,
    );
    return [total_orders, total_out_orders];
  }, [plan]);

  const onToggleSelection = useCallback(
    (id: string) => {
      if (selected.includes(id)) {
        setSelected(selected.filter((x) => x !== id));
      } else {
        setSelected([...selected, id]);
      }
    },
    [selected],
  );

  const onToggleAll = useCallback(() => {
    if (selected.length === plan?.routes.length) {
      setSelected([]);
    } else {
      setSelected(plan?.routes.map((x) => x.id) || []);
    }
  }, [selected, plan]);

  const startInduction = useCallback(() => {
    setShowInduction(true);
  }, []);

  const closeInduction = useCallback(() => {
    setShowInduction(false);
  }, []);

  const onSplit = useCallback((route: Route) => {
    setForSplit(route);
  }, []);

  const onCancelSplit = useCallback(() => {
    setForSplit(null);
  }, []);

  const onAcceptSplit = useCallback(
    async (routeId: string, vehicles: Partial<IVehiclesCapacities>) => {
      if (!plan) return;
      try {
        await http.lastmile.postSplitRoute(plan.plan_id, routeId, vehicles);
        setForSplit(null);
      } catch (e: any) {
        setError(e.response?.data?.message || e.message);
      } finally {
        onLoadPlan(id!);
      }
    },
    [plan, id, http, onLoadPlan],
  );

  const onAcceptPlan = useCallback(async () => {
    if (!plan || !station) return;
    try {
      setLoadingInduction(true);
      await http.lastmile.acceptPlan(plan.plan_id, station.station);
      await onLoadPlan(id!);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingInduction(false);
    }
  }, [plan, station, id, http, onLoadPlan]);

  const onMoveOrdersToRoute = useCallback(
    async (trackings: Array<number>, route_id: string) => {
      if (!plan) return;
      try {
        setLoadingInduction(true);
        await http.lastmile.postMoverdersToRoute(
          plan.plan_id,
          trackings,
          route_id,
        );
        await onLoadPlan(id!);
      } catch (e: any) {
        setError(e.response?.data?.message || e.message);
      } finally {
        setLoadingInduction(false);
      }
    },
    [plan, id, http, onLoadPlan],
  );

  const onRejectPlan = useCallback(async () => {
    if (!plan) return;
    try {
      setLoadingInduction(true);
      await http.lastmile.rejectPlan(plan.plan_id);
      await onLoadPlan(id!);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingInduction(false);
    }
  }, [plan, id, http, onLoadPlan]);

  const onStartJoinRoutes = useCallback(() => setShowJoinRoutes(true), []);
  const onCancelJoinRoutes = useCallback(() => setShowJoinRoutes(false), []);

  const onConfirmJoinRoutes = useCallback(async () => {
    if (!plan) return;
    try {
      setJoinRoutesIsLoading(true);
      await http.lastmile.postJoinRoutes(plan.plan_id, selected);
      await onLoadPlan(id!);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSelected([]);
      setShowJoinRoutes(false);
      setJoinRoutesIsLoading(false);
    }
  }, [plan, selected, id, http, onLoadPlan]);

  const onStartDisableRoute = useCallback(
    (routeId: string) => setForDisableRoute(routeId),
    [],
  );
  const onCancelDisableRoute = useCallback(() => setForDisableRoute(null), []);

  const onConfirmDisableRoute = useCallback(async () => {
    if (!plan || !forDisableRoute) return;
    try {
      setDisableLoading(true);
      await http.lastmile.postDisableRoute(plan.plan_id, forDisableRoute);
      await onLoadPlan(id!);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSelected([]);
      setForDisableRoute(null);
      setDisableLoading(false);
    }
  }, [plan, forDisableRoute, id, http, onLoadPlan]);

  useEffect(() => {
    if (id) onLoadPlan(id);
  }, [id, onLoadPlan]);

  return {
    plan,
    error,
    orders,
    onSplit,
    selected,
    forSplit,
    onToggleAll,
    onAcceptPlan,
    onRejectPlan,
    onAcceptSplit,
    onCancelSplit,
    disableLoading,
    startInduction,
    onDownloadPlan,
    show_induction,
    closeInduction,
    showJoinRoutes,
    forDisableRoute,
    loadingInduction,
    onDownloadOutPlan,
    onToggleSelection,
    onStartJoinRoutes,
    onCancelJoinRoutes,
    onConfirmJoinRoutes,
    onMoveOrdersToRoute,
    joinRoutesIsLoading,
    onStartDisableRoute,
    onCancelDisableRoute,
    onConfirmDisableRoute,
    stationCoordinates,
  };
};
